// src/components/dashboard/CalendarView.js - original version
import {
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Plus,
  RefreshCw,
  Settings,
  User
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const CalendarView = ({ 
  bookings = [],
  events = [],
  onDateSelect, 
  onBookingClick, 
  onCreateBooking, 
  selectedDate,
  calendarIntegrated = false,
  showIntegrationPrompt = false,
  loading = false 
}) => {
  // ==========================================
  // FIXED: STABLE STATE MANAGEMENT
  // ==========================================
  
  // FIXED: Create stable initial date - only calculated once
  const initialDate = useMemo(() => {
    return selectedDate ? new Date(selectedDate) : new Date();
  }, []); // Empty dependency array - only calculate once on mount

  // FIXED: Initialize state with stable values
  const [currentMonth, setCurrentMonth] = useState(() => 
    new Date(initialDate.getFullYear(), initialDate.getMonth(), 1)
  );
  
  const [selectedDay, setSelectedDay] = useState(() => initialDate);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [view, setView] = useState('month');

  // ==========================================
  // FIXED: STABLE UTILITY FUNCTIONS
  // ==========================================
  
  // FIXED: Memoize utility functions to prevent re-creation
  const getEventsForDate = useCallback((date) => {
    const dateStr = date.toISOString().split('T')[0];
    return calendarEvents.filter(event => {
      const eventDateStr = event.start.toISOString().split('T')[0];
      return eventDateStr === dateStr;
    });
  }, [calendarEvents]); // Only depends on calendarEvents

  const isToday = useCallback((date) => {
    const today = new Date();
    return isSameDay(date, today);
  }, []); // No dependencies - pure function

  const isSameDay = useCallback((date1, date2) => {
    return date1.toISOString().split('T')[0] === date2.toISOString().split('T')[0];
  }, []); // No dependencies - pure function

  // ==========================================
  // FIXED: STABLE EVENT HANDLERS
  // ==========================================
  
  const handleDateClick = useCallback((date) => {
    setSelectedDay(date);
    onDateSelect?.(date);
  }, [onDateSelect]); // Only depends on onDateSelect prop

  const handleEventClick = useCallback((event) => {
    console.log('Event clicked:', event);
    onBookingClick?.(event.booking);
  }, [onBookingClick]); // Only depends on onBookingClick prop

  const handleCreateBookingClick = useCallback((date) => {
    console.log('Create booking for date:', date);
    onCreateBooking?.(date);
  }, [onCreateBooking]); // Only depends on onCreateBooking prop

  // ==========================================
  // FIXED: STABLE selectedDate EFFECT
  // ==========================================
  
  // FIXED: Only update when selectedDate prop actually changes
  useEffect(() => {
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      const currentSelectedString = selectedDay.toISOString().split('T')[0];
      const newSelectedString = newDate.toISOString().split('T')[0];
      
      // Only update if the date actually changed
      if (newSelectedString !== currentSelectedString) {
        setSelectedDay(newDate);
        setCurrentMonth(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
      }
    }
  }, [selectedDate]); // FIXED: Only depend on selectedDate prop, not selectedDay

  // ==========================================
  // FIXED: STABLE CALENDAR EVENT PROCESSING
  // ==========================================
  
  useEffect(() => {
    console.log('📄 Processing calendar events and bookings...');
    const processedEvents = [];
    
    // Process calendar events (if integrated) - PRESERVE existing logic
    if (calendarIntegrated && Array.isArray(events)) {
      events.forEach(event => {
        if (event.start && event.title) {
          processedEvents.push({
            id: event.id || `event_${Date.now()}_${Math.random()}`,
            title: event.title,
            start: new Date(event.start),
            end: event.end ? new Date(event.end) : null,
            type: 'calendar',
            source: 'google_calendar',
            booking: event.extendedProps || {}
          });
        }
      });
    }
    
      // SURGICAL FIX: Process bookings with timezone-consistent parsing
  if (Array.isArray(bookings)) {
      bookings.forEach(booking => {
        if (booking.booking_date && booking.time) {
          let startDateTime, endDateTime;
          
          try {
            // SURGICAL FIX: Use timezone-consistent parsing instead of string-based parsing
            const dateField = booking.booking_date;
            const timeField = booking.time;
            
            // Parse date components separately to avoid timezone shifts
            const dateParts = dateField.split('-');
            const timeParts = timeField.split(':');
            
            if (dateParts.length === 3 && timeParts.length >= 2) {
              const year = parseInt(dateParts[0]);
              const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
              const day = parseInt(dateParts[2]);
              const hours = parseInt(timeParts[0]);
              const minutes = parseInt(timeParts[1]);
              
              // Create date using local timezone consistently
              startDateTime = new Date(year, month, day, hours, minutes);
              
              // Validate the parsed date
              if (isNaN(startDateTime.getTime()) ||
                  startDateTime.getFullYear() !== year ||
                  startDateTime.getMonth() !== month ||
                  startDateTime.getDate() !== day ||
                  startDateTime.getHours() !== hours ||
                  startDateTime.getMinutes() !== minutes) {
                console.warn('⚠️ Invalid date/time in booking for calendar:', booking.id, dateField, timeField);
                return; // Skip this booking
              }
              
              // Calculate end time
              const duration = booking.duration_minutes || booking.duration || 60;
              endDateTime = new Date(startDateTime.getTime() + (duration * 60000));
              
            } else {
              console.warn('⚠️ Invalid date/time format in booking for calendar:', booking.id, dateField, timeField);
              return; // Skip this booking
            }
          } catch (error) {
            console.warn('⚠️ Failed to parse booking date/time for calendar:', booking.id, error);
            return; // Skip this booking
          }
          
          // Add the successfully parsed booking event
          processedEvents.push({
            id: booking.id || `booking_${Date.now()}_${Math.random()}`,
            title: `${booking.service_name || 'Service'} - ${booking.customer_name || 'Customer'}`,
            start: startDateTime,
            end: endDateTime,
            type: 'booking',
            source: 'firebase',
            booking: booking,
            status: booking.status,
            synced: booking.calendar_event_id ? true : false
          });
        }
      });
    }
    
    console.log(`📊 Processed ${processedEvents.length} events (${events.length} calendar + ${bookings.length} bookings)`);
    console.log('📅 Events by date:', processedEvents.reduce((acc, event) => {
      const dateStr = event.start.toISOString().split('T')[0];
      acc[dateStr] = (acc[dateStr] || 0) + 1;
      return acc;
    }, {}));
    
    setCalendarEvents(processedEvents);
  }, [bookings, events, calendarIntegrated]); // FIXED: Stable dependencies

  // ==========================================
  // FIXED: STABLE CALENDAR NAVIGATION
  // ==========================================
  
  const navigateMonth = useCallback((direction) => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(prevMonth.getMonth() + direction);
      return newMonth;
    });
  }, []); // No dependencies needed

  const goToToday = useCallback(() => {
    const today = new Date();
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDay(today);
    onDateSelect?.(today);
  }, [onDateSelect]); // Only depends on onDateSelect prop

  // ==========================================
  // FIXED: STABLE CALENDAR DATA GENERATION
  // ==========================================
  
  const calendarData = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Get first day of month and how many days in month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    // Get days from previous month to fill the grid
    const prevMonth = new Date(year, month - 1, 0);
    const daysFromPrevMonth = startDayOfWeek;
    
    // Get days from next month to fill the grid
    const totalCells = Math.ceil((daysInMonth + startDayOfWeek) / 7) * 7;
    const daysFromNextMonth = totalCells - daysInMonth - daysFromPrevMonth;
    
    const calendarDays = [];
    
    // Previous month days
    for (let i = daysFromPrevMonth; i > 0; i--) {
      const day = prevMonth.getDate() - i + 1;
      const date = new Date(year, month - 1, day);
      calendarDays.push({
        date,
        day,
        isCurrentMonth: false,
        isPrev: true,
        events: getEventsForDate(date)
      });
    }
    
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      calendarDays.push({
        date,
        day,
        isCurrentMonth: true,
        isToday: isToday(date),
        isSelected: isSameDay(date, selectedDay),
        events: getEventsForDate(date)
      });
    }
    
    // Next month days
    for (let day = 1; day <= daysFromNextMonth; day++) {
      const date = new Date(year, month + 1, day);
      calendarDays.push({
        date,
        day,
        isCurrentMonth: false,
        isNext: true,
        events: getEventsForDate(date)
      });
    }
    
    return calendarDays;
  }, [currentMonth, selectedDay, getEventsForDate, isToday, isSameDay]); // FIXED: All stable dependencies

  // ==========================================
  // FIXED: STABLE RENDER FUNCTIONS
  // ==========================================
  
  const renderIntegrationPrompt = useCallback(() => {
    if (!showIntegrationPrompt || calendarIntegrated) return null;

    return (
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Calendar className="w-5 h-5 text-blue-500 mt-0.5 mr-3" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-900">
              Enhance Your Calendar Experience
            </h3>
            <p className="mt-1 text-sm text-blue-700">
              Connect your Google Calendar to sync appointments automatically and provide calendar subscription links for your clients.
            </p>
            <div className="mt-3">
              <Link 
                to="/dashboard?tab=calendar-integration"
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Settings className="w-4 h-4 mr-2" />
                Set Up Calendar Integration
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }, [showIntegrationPrompt, calendarIntegrated]);

  const renderCalendarHeader = useCallback(() => {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    return (
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h2>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-gray-100 rounded-md"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-gray-100 rounded-md"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={goToToday}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Today
          </button>
          {calendarIntegrated && (
            <div className="flex items-center text-sm text-green-600">
              <CheckCircle className="w-4 h-4 mr-1" />
              Google Calendar Integrated
            </div>
          )}
        </div>
      </div>
    );
  }, [currentMonth, navigateMonth, goToToday, calendarIntegrated]);

  const renderCalendarGrid = useCallback(() => {
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <div className="bg-white rounded-lg shadow-sm border">
        {/* Week day headers */}
        <div className="grid grid-cols-7 border-b">
          {weekDays.map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar days */}
        <div className="grid grid-cols-7">
          {calendarData.map((dayData, index) => (
            <div
              key={index}
              className={`
                min-h-[100px] p-2 border-r border-b cursor-pointer transition-colors
                ${dayData.isCurrentMonth ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 text-gray-400'}
                ${dayData.isToday ? 'bg-blue-50' : ''}
                ${dayData.isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''}
              `}
              onClick={() => handleDateClick(dayData.date)}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`
                  text-sm font-medium
                  ${dayData.isToday ? 'text-blue-600' : ''}
                  ${dayData.isSelected ? 'text-blue-600' : ''}
                `}>
                  {dayData.day}
                </span>
                {dayData.events.length > 0 && (
                  <span className="text-xs text-gray-500">
                    {dayData.events.length}
                  </span>
                )}
              </div>
              
              {/* Events for this day */}
              <div className="space-y-1">
                {dayData.events.slice(0, 3).map(event => (
                  <div
                    key={event.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEventClick(event);
                    }}
                    className={`
                      text-xs p-1 rounded cursor-pointer truncate
                      ${event.type === 'calendar' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}
                      hover:opacity-80
                    `}
                    title={event.title}
                  >
                    <div className="flex items-center">
                      {event.type === 'calendar' ? (
                        <ExternalLink className="w-3 h-3 mr-1" />
                      ) : (
                        <User className="w-3 h-3 mr-1" />
                      )}
                      <span className="truncate">{event.title}</span>
                    </div>
                  </div>
                ))}
                {dayData.events.length > 3 && (
                  <div className="text-xs text-gray-500 text-center">
                    +{dayData.events.length - 3} more
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }, [calendarData, handleDateClick, handleEventClick]);

  const renderCalendarFooter = useCallback(() => {
    return (
      <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-blue-100 rounded mr-2"></div>
            <span>Bookings ({bookings.length})</span>
          </div>
          {calendarIntegrated && (
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-100 rounded mr-2"></div>
              <span>Calendar Events ({events.length})</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleCreateBookingClick(selectedDay)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4 mr-1" />
            New Booking
          </button>
        </div>
      </div>
    );
  }, [bookings.length, events.length, calendarIntegrated, selectedDay, handleCreateBookingClick]);

  // ==========================================
  // MAIN RENDER
  // ==========================================
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading calendar...</span>
      </div>
    );
  }

  return (
    <div className="calendar-view">
      {/* Integration Prompt */}
      {renderIntegrationPrompt()}
      
      {/* Calendar Header */}
      {renderCalendarHeader()}
      
      {/* Calendar Grid */}
      {renderCalendarGrid()}
      
      {/* Calendar Footer */}
      {renderCalendarFooter()}
    </div>
  );
};

export default CalendarView;
